import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import ActionCenterTabs from '../ActionCenterTabs';
import { HOME_ROUTE } from '../../../lib/constants/route';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import get_action_proposals_api_call from '../../../lib/api/actions/get-action-proposals';
import { ActionProposal } from '../../../lib/api/actions/action-types';
import ActionProposalCard from '../actionQueue/ActionProposalCard';
import { PROPOSAL_PLATFORM_OPTIONS } from '../actionQueue/proposalFormat';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import {
  HISTORY_STATUSES,
  HistoryLoadState,
  failedHistoryStatuses,
  historyLoadState,
  mergeHistoryPages,
} from './historyList';
import '../actions.scss';

const ActionHistory = () => {
  const { t } = useTranslation();

  const [proposals, setProposals] = useState<ActionProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [platformFilter, setPlatformFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // Whether every request for this page of results came back: a failed one
  // must not pass for an empty or complete list.
  const [loadState, setLoadState] = useState<HistoryLoadState>('ok');

  const statusTabs = [
    { label: t('All'), value: '' },
    { label: t('Completed'), value: 'completed' },
    { label: t('Failed'), value: 'failed' },
    { label: t('Denied'), value: 'denied' },
    { label: t('Expired'), value: 'expired' },
    { label: t('RolledBack'), value: 'rolled_back' },
  ];

  const fetchProposals = useCallback(async () => {
    try {
      const fetchStatus = (status: string) =>
        get_action_proposals_api_call({
          status,
          platform: platformFilter || undefined,
          page: currentPage,
          per_page: 20,
        });

      // Without a filter the list is every finished status. The endpoint
      // matches one status exactly, so each is asked for and the pages merged.
      const statuses: readonly string[] = statusFilter
        ? [statusFilter]
        : HISTORY_STATUSES;
      const pages = await Promise.all(statuses.map(fetchStatus));
      const result = mergeHistoryPages(pages);

      setProposals(result.data);
      setTotalPages(result.last_page);
      setLoadState(
        historyLoadState(
          failedHistoryStatuses(pages, statuses).length,
          statuses.length
        )
      );
    } catch (error) {
      console.error('Failed to load action history:', error);
      setProposals([]);
      setTotalPages(1);
      setLoadState('failed');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, platformFilter, currentPage]);

  useEffect(() => {
    setLoading(true);
    setCurrentPage(1);
  }, [statusFilter, platformFilter]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  const retry = () => {
    setLoading(true);
    fetchProposals();
  };

  const retryButton = (
    <MainSquareButton
      value={t('Retry')}
      type='button'
      buttonType='white'
      onClick={retry}
    />
  );

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages: number[] = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }

    return (
      <div className='action-pagination'>
        <button
          className='action-pagination__btn'
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
        >
          {t('Previous')}
        </button>
        {pages.map((page) => (
          <button
            key={page}
            className={`action-pagination__btn ${
              page === currentPage ? 'action-pagination__btn--active' : ''
            }`}
            onClick={() => setCurrentPage(page)}
          >
            {page}
          </button>
        ))}
        <button
          className='action-pagination__btn'
          disabled={currentPage === totalPages}
          onClick={() =>
            setCurrentPage((prev) => Math.min(totalPages, prev + 1))
          }
        >
          {t('Next')}
        </button>
      </div>
    );
  };

  return (
    <SubPageInnerContentHolder
      title={t('ActionHistory')}
      backBtnLink={HOME_ROUTE}
    >
      <ActionCenterTabs />
      {/* Filters */}
      <div className='action-queue-filters'>
        <div className='action-queue-filters__status-group'>
          {statusTabs.map((tab) => (
            <button
              key={tab.value}
              className={`action-queue-filters__filter-btn ${
                statusFilter === tab.value
                  ? 'action-queue-filters__filter-btn--active'
                  : ''
              }`}
              onClick={() => setStatusFilter(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <select
          className='action-queue-filters__platform-select'
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value)}
        >
          <option value=''>{t('AllPlatforms')}</option>
          {PROPOSAL_PLATFORM_OPTIONS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <LoadingSpinner customLoadingMsg={t('LoadingActionHistory')} />
        </div>
      ) : loadState === 'failed' ? (
        <div className='action-empty-state' role='alert'>
          <div className='action-error' style={{ textAlign: 'left' }}>
            <p className='action-error__text'>
              <strong>{t('FailedToLoadActionHistory')}</strong>
            </p>
          </div>
          <div style={{ marginTop: '1.2rem' }}>{retryButton}</div>
        </div>
      ) : proposals.length === 0 && loadState === 'ok' ? (
        <div className='action-empty-state'>
          <p className='action-empty-state__text'>
            {t('NoActionHistoryFound')}
          </p>
        </div>
      ) : (
        <>
          {loadState === 'partial' && (
            <div className='action-error' role='alert'>
              <p className='action-error__text'>
                {t('ActionHistorySomeNotLoaded')}
              </p>
              <div style={{ marginTop: '0.8rem' }}>{retryButton}</div>
            </div>
          )}
          {proposals.map((proposal) => (
            <ActionProposalCard
              key={proposal.id}
              proposal={proposal}
              showActions={false}
            />
          ))}
          {renderPagination()}
        </>
      )}
    </SubPageInnerContentHolder>
  );
};

export default ActionHistory;
